import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Input, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { UserPermissionsService } from '@app/core/user-permissions/user-permissions.service';
import { PropertyPricingService, TaxSelectionComponent } from '@app/features/pricing-and-availability';
import { PropertyUpdateStatusComponent } from '@app/features/properties';
import { DirectPlan } from '@app/modules/direct/models/direct-subscription.interface';
import { DirectSubscriptionService } from '@app/modules/direct/services/direct-subscription.service';
import {
  AdyenPayoutMethodStatus,
  PayoutMethod,
  PayoutMethodProvider,
  PayoutMethodsService,
} from '@app/modules/direct/services/payout-methods.service';
import { PlanId } from '@app/shared/+state/billing';
import { OnDestroyComponent } from '@app/shared/components/on-destroy/on-destroy.component';
import { PropertyUpdateStatusByPlatform } from '@app/shared/interfaces/lib/property.interface';
import { DEFAULT_CALCULATION_TYPE, PricesAndFees, Tax } from '@app/shared/models/tax';
import { PosthogService } from '@app/shared/services/posthog/posthog.service';
import { Property } from '@app/shared/services/properties/properties.service';
import { TaxService } from '@app/shared/services/taxes/tax.service';
import { TranslocoRootModule } from '@app/transloco-root.module';
import { PaymentCardAction, PayoutMethodCardComponent } from '@app/ui/payout-method-card/payout-method-card.component';
import { UiModule } from '@app/ui/ui.module';
import {
  CurrencyCodePipe,
  IconAirbnbComponent,
  IconBookingComponent,
  IconComponent,
  LoadingSpinnerComponent,
} from '@ui';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { debounceTime, first, map, takeUntil } from 'rxjs/operators';

@Component({
  standalone: true,
  selector: 'sbnb-property-fees-taxes-discounts',
  templateUrl: './property-fees-taxes-discounts.component.html',
  styleUrls: ['./property-fees-taxes-discounts.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    UiModule,
    TranslocoRootModule,
    PropertyUpdateStatusComponent,
    TaxSelectionComponent,
    CurrencyCodePipe,
    PayoutMethodCardComponent,
    IconAirbnbComponent,
    IconBookingComponent,
    IconComponent,
    LoadingSpinnerComponent,
  ],
})
export class PropertyFeesTaxesDiscountsComponent extends OnDestroyComponent implements OnInit, AfterViewInit {
  @Input() property: Property;
  @Input() section: 'security-deposit' | 'occupancy-based' | 'fees' | 'discounts';
  @ViewChildren('section') sections: QueryList<ElementRef>;

  // Make AdyenPayoutMethodStatus available in template
  AdyenPayoutMethodStatus = AdyenPayoutMethodStatus;

  public fees = PricesAndFees.fees;
  public discounts = PricesAndFees.discounts;
  public occupancyBased = PricesAndFees.occupancyBased;
  enableTaxes = false;
  taxes: Tax[];
  taxesUuids: string[];
  public loading$ = this.propertyDetailService.loadingPricingFeeDiscount$;
  public priceSaveState$ = this.propertyDetailService.priceSaveState$;
  public featureFlagAirbnbDiscountsEnabled$ = this.posthogService.airbnbDiscountsEnabled$;
  public featureFlagAirbnbSecurityDepositsEnabled$ = this.posthogService.airbnbSecurityDeposits$;
  public featureFlagBookingComSecurityDepositsEnabled$ = this.posthogService.bookingComSecurityDeposits$;
  public isPremium$ = this.directSubscription.isPremium$;
  public allowAutomaticSecurityDeposits$ = new BehaviorSubject<boolean>(false);
  public securityDepositRestrictionReason$ = new BehaviorSubject<string | null>(null);

  public displayAutomaticSecurityDeposit$ = this.authService.user$.pipe(
    map((user) => user.direct_subscription_plan === DirectPlan.Premium || user.plan !== PlanId.Legacy),
    takeUntil(this.destroyed$)
  );

  private sectionScrollSubscription: Subscription | null = null;

  public pricingFeeDiscountData$ = this.propertyDetailService.pricingFeeDiscountData$.pipe(
    map((data) => {
      // Set the calculation type to per-reservation for fees that have a value but no calculation type
      const fees = ['cleaning_fee', 'resort_fee', 'community_fee', 'linens_fee', 'management_fee', 'pet_fee'];
      for (const fee of fees) {
        if (data[fee].value === null) {
          data[fee] = {
            value: 0,
            type: 'fixed',
            calculation_type: DEFAULT_CALCULATION_TYPE,
          };
        } else {
          /*
           * This is a temporary modification while the backend supports both old & new frontend
           * versions. This page is _the_ old frontend, but the `CalculationType`s have changed
           * to support new values in the new page. This whole page will be removed once the
           * new page is fully rolled out.
           */
          data[fee].calculation_type =
            {
              'per-reservation': 'per-stay',
              'per-person-per-stay': 'per-guest',
              'per-guest-per-stay': 'per-guest',
              'per-person-per-reservation': 'per-guest',
              'per-guest-per-reservation': 'per-guest',
              'per-pet-per-reservation': 'per-pet-per-stay',
              'per-person': 'per-guest',
              'per-person-per-night': 'per-guest-per-night',
            }[data[fee].calculation_type] ?? data[fee].calculation_type;
        }
      }

      // Transform early bird discount to array if it's not already
      if (data.early_bird_discount && !Array.isArray(data.early_bird_discount)) {
        data.early_bird_discount = [];
      }

      if (data.last_minute_discount && !Array.isArray(data.last_minute_discount)) {
        data.last_minute_discount = [];
      }

      return {
        ...data,
        early_bird_discount: data.early_bird_discount || [],
        last_minute_discount: data.last_minute_discount || [],
        person_capacity: {
          type: 'fixed',
          value: this.property.person_capacity,
          edited: false,
        },
      };
    })
  );

  public showIconLegend = false;
  public status = PropertyUpdateStatusByPlatform;

  constructor(
    private taxService: TaxService,
    public authService: AuthenticationService,
    private directSubscription: DirectSubscriptionService,
    private permissionsService: UserPermissionsService,
    private readonly propertyDetailService: PropertyPricingService,
    private posthogService: PosthogService,
    private readonly payoutMethodsService: PayoutMethodsService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    super();
  }

  ngOnInit(): void {
    this.enableTaxesCheck();
    this.checkSecurityDepositsPaymentConditions();
  }

  ngAfterViewInit(): void {
    // Observe route parameters for section changes
    this.route.queryParams
      .pipe(
        map((params) => params['section']),
        takeUntil(this.destroyed$)
      )
      .subscribe((section) => {
        if (section) {
          this.registerSectionScroll(section);
        }
      });
  }

  getTaxes() {
    this.taxService.getTaxes().subscribe((taxes) => {
      this.taxes = taxes.filter((tax) => tax.properties.includes(this.property.id));
      this.taxesUuids = this.taxes.map((tax) => tax.uuid);
    });
  }

  enableTaxesCheck() {
    combineLatest([this.directSubscription.isBasic$, this.permissionsService.isPrimaryUser()])
      .pipe(first(([isBasic, isPrimary]: [boolean, boolean]) => isBasic && isPrimary))
      .subscribe(([isBasic, isPrimary]: [boolean, boolean]) => {
        this.enableTaxes = true;
        this.getTaxes();
      });
  }

  savePriceChange(key: string, dataToUpdate: any) {
    this.propertyDetailService.savePriceChanges({ key, data: dataToUpdate });
  }

  invlidateIncompatibleCalculationType(key: string, currentState: any, type: 'fixed' | 'percent') {
    const calcType = this.fees
      .find((fee) => fee.key === key)
      ?.calculationTypes?.find((ct) => ct.value === currentState.calculation_type);
    if (!calcType?.[currentState.type]) {
      currentState.calculation_type = 'per-reservation';
    }
  }

  /* Discount helper methods | Airbnb update */

  /* Add new discount, locally only */
  addDiscount(currentData: any, key: string) {
    if (!currentData[key]) {
      currentData[key] = [];
    }

    currentData[key].push({
      type: 'percent',
      value: 0,
      threshold_days: 1,
      local: true,
    });
  }

  /* Remove discount, send a request only if it is not marked as local only */
  removeDiscount(key: string, data: any[], index: number) {
    const removed = data.splice(index, 1);
    if (!removed?.[0]?.local) {
      this.checkAndSave(key, data);
    }
  }

  /* OnChanges, check if we should submit a request, if there is an error the current field will be highlighted */
  // TODO: when in forms, all fields will be checked & highlighted
  checkAndSave(key: string, data: any[]) {
    const submitted = [];
    for (const item of data) {
      if (!this.validateAirbnbDiscounts(key, item)) return;
      if (item.local) {
        if (item.value > 0 && item.threshold_days > 0) {
          delete item.local;
          submitted.push(item);
        }
      } else {
        if (!item.value || !item.threshold_days) {
          // error case, do nothing, ui will highlight the error field
          return;
        }
        submitted.push(item);
      }
    }

    const payload = submitted.map((item) => {
      if ('edited' in item) {
        delete item.edited;
      }
      return item;
    });

    this.savePriceChange(key, payload);
  }

  validateAirbnbDiscounts(key: string, data: { threshold_days: number; value: number }) {
    if (key === 'last_minute_discount') {
      if (data.threshold_days < 0 || data.threshold_days > 28 || data.value <= 0 || data.value > 100) {
        return false;
      }
    }

    if (key === 'early_bird_discount') {
      if (data.threshold_days < 0 || data.threshold_days > 24 || data.value <= 0 || data.value > 100) {
        return false;
      }
    }
    return true;
  }

  checkSecurityDepositsPaymentConditions() {
    this.payoutMethodsService.fetchPayoutMethods().subscribe((payoutMethods) => {
      const isPayoutMethodAvailable = payoutMethods
        .filter((payoutMethod) => payoutMethod.provider === PayoutMethodProvider.ADYEN)
        .some((payoutMethod) => payoutMethod.all_properties);
      if (!isPayoutMethodAvailable) {
        this.allowAutomaticSecurityDeposits$.next(false);
        this.securityDepositRestrictionReason$.next('Add payout method to unlock.');
        return;
      }

      const isCurrencyValidForSecurityDeposit = ['USD', 'AUD', 'GBP'].includes(this.property?.currency);
      if (!isCurrencyValidForSecurityDeposit) {
        this.allowAutomaticSecurityDeposits$.next(false);
        this.securityDepositRestrictionReason$.next(
          'Property currency must be USD, AUD or GBP. Additional currencies coming soon.'
        );
        return;
      }

      this.allowAutomaticSecurityDeposits$.next(true);
      this.securityDepositRestrictionReason$.next(null);
    });
  }

  getPayoutMethodStatus(payoutMethod: PayoutMethod): AdyenPayoutMethodStatus {
    if (payoutMethod.provider === 'stripe-oauth') {
      return AdyenPayoutMethodStatus.VALID;
    }
    return payoutMethod.adyen?.status || AdyenPayoutMethodStatus.PENDING;
  }

  getPayoutMethodActions(payoutMethod: PayoutMethod): PaymentCardAction[] {
    // Add reset action when a payout method is assigned
    return [
      { id: 'edit', label: 'Choose a different payout method' },
      { id: 'reset', label: 'Reset to payout method for property' },
    ];
  }

  onPayoutMethodActionClicked(payoutMethod: PayoutMethod | null, actionId: string) {
    switch (actionId) {
      case 'edit':
        // Navigate to the payout method assignment page
        this.router.navigate(['/settings/payouts/assign', 'security_deposit_property', this.property.id]);
        break;
      case 'reset':
        // Reset the payout method to null (property default) using the DELETE endpoint
        this.payoutMethodsService
          .deletePayoutMethodForResource('security_deposit_property', this.property.id.toString())
          .subscribe(
            () => {
              this.property.security_deposit_payout_method = null;
              this.property.security_deposit_pay_method_uuid = null;
            },
            (error) => {
              console.error('Error resetting payout method:', error);
            }
          );
        break;
      default:
        break;
    }
  }

  registerSectionScroll(section: string) {
    if (this.sectionScrollSubscription) {
      this.sectionScrollSubscription.unsubscribe();
      this.sectionScrollSubscription = null;
    }

    this.sectionScrollSubscription = this.sections.changes
      .pipe(takeUntil(this.destroyed$), debounceTime(500))
      .subscribe((sections) => {
        const scrollSection = sections.find((s) => s.nativeElement.id === section);
        if (scrollSection) {
          scrollSection.nativeElement.scrollIntoView({ behavior: 'smooth' });
          if (this.sectionScrollSubscription) {
            this.sectionScrollSubscription.unsubscribe();
            this.sectionScrollSubscription = null;
          }
        }
      });
  }
}
